import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import RouterBreadcrums from 'components/shared/RouterBreadcrumbs';
import DefaultSidebar from 'components/shared/DefaultSidebar';
import { Box, styled } from '@mui/material';
import ScrollToTop from 'pages/shared/ScrollToTop';
import DashboardNavbar from './DashboardNavbar';
import usePath from '../../components/shared/usePath';
import FloatingComment from 'src/pages/shared/FloatingComment';
import FloatingCommentWithModal from 'src/pages/shared/FloatingCommentWithModal';
import useAuth from 'src/hooks/useAuth';
import { AxiosConfigurator } from 'src/utils/axiosConfigurator';
import { UserProfiles } from 'src/types/claims';
import { PermissionLevel } from 'src/types/profilePermission';

interface DashboardLayoutProps {
  children?: ReactNode;
}

const DashboardLayoutRoot = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  // overflow: 'auto',
  // height: '100vh',
  // width: '100vw',
  // maxWidth: '100% !important',
  // paddingX: '0px !important',
  minHeight: '100dvh',
  display: 'grid',
  gridTemplateRows: 'auto 1fr',
}));

interface LayoutWrapperI {
  open: boolean;
  onHome: boolean;
}
const DashboardLayoutWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'onHome',
  name: 'DashboardLayoutWrapper',
  slot: 'Wrapper',
})<LayoutWrapperI>(({ theme, open, onHome }) => ({
  backgroundColor: theme.palette.background.default,
  // overflow: 'auto',
  paddingBottom: '20px',
  paddingTop: onHome ? '0px' : '60px',
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DashboardLayout: FC<DashboardLayoutProps> = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const { user } = useAuth();
  const stateHandler = () => setIsSidebarOpen(!isSidebarOpen);
  const { pathnames, breadcrumbNameMap, isPath } = usePath();

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  return (
    <DashboardLayoutRoot>
      <DashboardNavbar onSidebarOpen={stateHandler} />
      <DefaultSidebar
        onClose={stateHandler}
        open={isSidebarOpen}
      />
      <DashboardLayoutWrapper
        open={isSidebarOpen}
        onHome={!isPath}
      >
        <AxiosConfigurator />
        <Box
          px={2}
          py={2}
          sx={{ display: { xs: 'none', lg: 'block' } }}
        >
          {!!isPath && (
            <RouterBreadcrums
              pathnames={pathnames}
              breadcrumbNameMap={breadcrumbNameMap}
            />
          )}
        </Box>
        <>
          <Outlet />
          {isPath !== undefined && <ScrollToTop />}
          {user?.claims.profile === UserProfiles.Conductor &&
            user?.claims.ObservacionesPlanilladeRuta !== PermissionLevel.None &&
            isMobile && <FloatingComment isPath={isPath} />}
          {isPath === undefined &&
            user?.claims.profile === UserProfiles.Conductor &&
            user?.claims.ObservacionesPlanilladeRuta !== PermissionLevel.None &&
            !isMobile && <FloatingCommentWithModal />}
        </>
      </DashboardLayoutWrapper>
    </DashboardLayoutRoot>
  );
};

export default DashboardLayout;
